import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { DateObject } from '@uplift/services';
import { useCallback, useMemo } from 'react';
import { AsyncButton, Button } from '@uplift/ui-v2';
import { MatchingProfileData } from '@uplift/api';

export type ConfirmDialogProps = {
  appointmentTime: DateObject;
  provider: MatchingProfileData;
  isLoading: boolean;
  onClose: (confirm?: boolean) => Promise<void>;
};

export const ConfirmDialog: React.VFC<ConfirmDialogProps> = ({ appointmentTime, provider, isLoading, onClose }) => {
  const l10n = useL10n();
  const { classes } = useConfirmDialogStyles();
  const userName = provider?.nameDisplay;

  const displayName = useMemo(() => {
    if (!userName) {
      return '';
    }

    return userName.defaultDisplay;
  }, [ userName ]);

  const handleClose = useCallback(async (confirm?: boolean) => {
    await onClose(confirm);
  }, [ onClose ]);

  return (
    <Dialog open onClose={() => handleClose(false)}>
      <DialogTitle>{l10n.findPsychiatrist.confirmConsultation}</DialogTitle>
      <DialogContent>
        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" gap=".5rem">
          <img src={provider.avatarAsset?.url} alt="provider avatar" className={classes.confirmAvatar} />
          <Typography variant="body1">
            {displayName}
          </Typography>
          <Typography variant="body1" color="secondary" component="b" fontWeight={500}>
            {appointmentTime.format('dddd')} at {appointmentTime.format('h:mm a')}
          </Typography>
        </Box>
        <Box mt={5} mb={1} display="flex" flexDirection="column" gap=".5rem">
          <AsyncButton variant="contained" color="secondary" fullWidth onClick={async () => handleClose(true)} loading={isLoading}>
            {l10n.findPsychiatrist.confirm}
          </AsyncButton>
          <Button fullWidth onClick={() => handleClose(false)}>
            {l10n.findPsychiatrist.cancel}
          </Button>
        </Box>
        <Typography color="textSecondary">
          {l10n.findPsychiatrist.billed}
        </Typography>
        <br />
        <Typography color="textSecondary" fontSize=".75em" fontStyle="italic">
          {l10n.findPsychiatrist.noCSWarning}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const useConfirmDialogStyles = makeStyles({ name: 'ConfirmDialog' })({
  confirmAvatar: {
    borderRadius: '4px',
    height: '128px',
    width: '128px'
  },
});
